/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import useStyles from "./style";
import CloseIcon from '@material-ui/icons/Close';
import GreyButton from "../../../../elements/GreyButton";
import PrimaryButton from "../../../../elements/PrimaryButton";
import { ApiKeysReducer, ApiKeyV2WithKey, ApplicationState } from "../../../../data/interfaces";
import { connect } from "react-redux";
import { revokeApiKeyV2 } from "../../../../data/operations";
import { useIntl } from "react-intl";
import LoadingLogo from "./LoadingLogo";

interface APIKeyRegenerationConfirmation {
    open: boolean;
    apiKeysReducer: ApiKeysReducer;
    selectedApiKey: string;

    delelteApiKey: (apiKeyId: string) => void,
    onClose: () => void;
}

function APIKeyDeletionConfirmation(props: APIKeyRegenerationConfirmation) {
    const classes = useStyles();
    const intl = useIntl();
    const { open, apiKeysReducer, selectedApiKey, onClose, delelteApiKey } = props;

    const [isLoading, setIsLoading] = useState(false);

    const [isDeletionInitiated, setIsDeletionInitiated] = useState(false);

    const [isDeletionSuccess, setIsDeletionSuccess] = useState(false);

    const confirmDeleteKey = () => {
        delelteApiKey(selectedApiKey);
        setIsDeletionInitiated(true);
        setIsLoading(true);
    }

    useEffect(() => {
        if (isDeletionInitiated && !apiKeysReducer.isKeyDeleting && apiKeysReducer.deleteApiKeyError === '') {
            setIsDeletionSuccess(true);
            setIsDeletionInitiated(false);
            setIsLoading(false);
        } else if (isDeletionInitiated && !apiKeysReducer.isKeyDeleting && apiKeysReducer.deleteApiKeyError !== '') {
            onClose();
            setIsDeletionInitiated(false);
            setIsLoading(false);
        }
    }, [apiKeysReducer.isKeyDeleting]);


    const ConfirmationForm = () => {
        return (
            <Box className={classes.formContainer}>
                <Typography>
                    {intl.formatMessage({
                        id: 'apikeys.delete.confirmation.text1',
                        defaultMessage: "Deleting the API key will impact applications using the current key."
                    })}{' '}
                    {intl.formatMessage({
                        id: 'apikeys.delete.confirmation.text4',
                        defaultMessage: "Are you sure you want to proceed? This action cannot be undone."
                    })}
                </Typography>
            </Box>
        );
    };

    const DeletionSuccessMessage = () => {
        return (
            <Box className={classes.formContainer}>
                <Typography>
                    {intl.formatMessage({
                        id: 'apikeys.delete.success.message',
                        defaultMessage: "API Key has been deleted successfully."
                    })
                    }
                </Typography>
            </Box>
        );
    }

    const ActionFooter = () => {
        return (
            <Box pb={2} mt={3} flexDirection="row" justifyContent="flex-end" display="flex">
                <GreyButton
                    onClick={onClose}
                    className={classes.cancelBtn}
                    buttonLabel={"Close"}
                />
                { !isDeletionSuccess &&
                    <PrimaryButton
                        onClick={confirmDeleteKey}
                        buttonLabel={"Delete"}
                        className={classes.regenerateBtn}
                        id="create-button"
                    />
                }
            </Box>
        );
    }

    return (
        <React.Fragment>
            <Dialog
                    open={open}
                    onClose={(_,reason) => {
                        if (reason !== 'backdropClick') {
                            onClose()
                        }
                    }}
                    aria-labelledby="delete-app-dialog-title"
                    fullWidth={!isLoading}
                    classes={{ paper: classes.customWidth }}
                >
                    <DialogTitle className={classes.dialogTitle}>
                        { !isLoading &&
                            <Typography className={classes.primaryTitle}>
                                Delete API Key
                            </Typography>
                        }
                        {
                            !isLoading && onClose ? (
                                <IconButton className={classes.closeButton} onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            ) : null
                        }
                    </DialogTitle>
                    <DialogContent classes={{ root: classes.createApiDialogContent }}>
                        {  isLoading && <LoadingLogo />}
                        { !isLoading && !isDeletionSuccess && <ConfirmationForm />}
                        { !isLoading && isDeletionSuccess && <DeletionSuccessMessage />}
                        { !isLoading && <ActionFooter />}
                    </DialogContent>
                </Dialog>
        </React.Fragment>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiKeysReducer: state.apiKeysReducer,
    }
}

const mapDispatchToProps = {
    delelteApiKey: revokeApiKeyV2,
};

export default connect(mapStateToProps, mapDispatchToProps)(APIKeyDeletionConfirmation);
